import { Component, OnInit } from '@angular/core';
import { StudyNavigationService } from '../../services/study-navigation.service';
import { LoadConfigService } from '../../services/load-config.service';
import { UtilsService } from '../../services/utils.service';
import { InstructionService } from '../../services/instruction.service';

type TimelineInfo = { section: string; idx: number };

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    standalone: false
})
export class OverviewComponent implements OnInit {
  nextIAT: number;
  config;
  thumbnails = [2, 3, 4, 5, 6].map(num => `Illustration_Thumbnail_Bl_${num}_notxt.png`);

  relevantSections: { section: string; idx: number }[] = [];
  sections: string[] = [];

  continueButtonLabel: string;
  overviewTitle: string;

  constructor(
    private navigationService: StudyNavigationService,
    private utilsService: UtilsService,
    private loadConfigService: LoadConfigService,
    private instructionService: InstructionService
  ) {}

  ngOnInit(): void {
    this.nextIAT = this.navigationService.getTestId();
    this.config = this.loadConfigService.getClientConfig();
    const timeline = this.navigationService.getTimeline();

    const filteredSections = this.getRelevantSections(timeline);
    this.relevantSections = this.mapTimelineToStudyInfo(filteredSections);

    this.continueButtonLabel = this.instructionService.getTranslationsForTerm('OVERVIEW_CONTINUE_BUTTON');

    this.overviewTitle = this.instructionService.getTranslationsForTerm('OVERVIEW_TITLE');
  }

  private mapTimelineToStudyInfo(relevantSections: TimelineInfo[]) {
    const studyIdentifiers = ['IAT', 'priming', 'persona', 'conjoint', 'brand', 'agreement', 'modular'];

    // get new display name for study sections
    const studySections = relevantSections.filter(info => {
      if (studyIdentifiers.includes(info.section)) {
        return true;
      }
      return false;
    });

    const studySectionsToIdx = studySections.map((info, idx) => {
      const studyInfo = this.loadConfigService.getRandomizedTestConfigByIdx(idx);

      // eslint-disable-next-line no-prototype-builtins
      const hasDisplayName = studyInfo['displayName'].hasOwnProperty(this.instructionService.getLanguageIdentifier());
      const displayName = hasDisplayName
        ? studyInfo['displayName'][this.instructionService.getLanguageIdentifier()]
        : info.section;
      return {
        ...info,
        section: displayName
      };
    });

    // get new display name for non-study sections
    const nonStudySections = relevantSections.filter(info => {
      if (studyIdentifiers.includes(info.section)) {
        return false;
      }
      return true;
    });

    const modifiedNonStudySections = nonStudySections.map(info => {
      switch (info.section) {
        case 'introduction': {
          return {
            ...info,
            section: this.getTranslation(info.section, 'OVERVIEW_INTRODUCTION_TITLE')
          };
        }
        case 'questionnaire': {
          return {
            ...info,
            section: this.getTranslation(info.section, 'OVERVIEW_QUESTIONNAIRE_TITLE')
          };
        }
        case 'final': {
          return {
            ...info,
            section: this.getTranslation(info.section, 'OVERVIEW_FINAL_TITLE')
          };
        }
        default: {
          return info;
        }
      }
    });

    return this.mergeSections(studySectionsToIdx, modifiedNonStudySections).map(info => {
      return {
        ...info,
        section: this.utilsService.capitalizeWord(info.section)
      };
    });
  }

  private mergeSections(one: TimelineInfo[], two: TimelineInfo[]) {
    const merged = [];
    let i = 0;
    let j = 0;

    while (i < one.length && j < two.length) {
      if (one[i].idx < two[j].idx) {
        merged.push(one[i]);
        i++;
      } else {
        merged.push(two[j]);
        j++;
      }
    }

    if (i < one.length) {
      merged.push(...one.slice(i));
    }

    if (j < two.length) {
      merged.push(...two.slice(j));
    }

    return merged;
  }

  private getRelevantSections(timeline: string[]): TimelineInfo[] {
    const indexedTimeline = timeline.map((section, idx) => {
      return {
        section,
        idx
      };
    });

    const irrelevantSections = this.loadConfigService.getIrrelevantSections();

    const relevantSections = indexedTimeline.filter(section => {
      return !irrelevantSections.includes(section.section);
    });

    return relevantSections;
  }

  private getTranslation(section: string, term: string) {
    const translation = this.instructionService.getTranslationsForTerm(term);
    if (typeof translation !== undefined) {
      return translation;
    }
    return section;
  }

  getTimelineIdx() {
    return this.navigationService.getTimelineIdx();
  }
}
